import { Link } from 'react-router-dom';
import { useState } from 'react';
import ProductionCorrectionsModal from '../../RecipeDisplay/components/ProductionCorrectionsModal';
import Select from '../../../components/ui/Select';
import { useTranslation } from '../../../i18n/i18n';
import Speedometer from '../../../assets/icons/speedometer';
import { ReactComponent as CloudDoneOutline } from '../../../assets/icons/cloud_done_outline.svg';
import { ReactComponent as CloudOfflineOutline } from '../../../assets/icons/cloud_offline_outline.svg';
import { cn } from '../../../utils/cn';
import { ChevronLeft, Clipboard, Menu, Minus, Pen, Plus } from 'lucide-react';
import useOnlineStatus from '../../../hooks/useOnlineStatus';
import { useRecipeDisplay } from '../../../context/RecipeDisplayContext';

/**
 * TopNavigation component for the FeedTimestamping page
 * Displays conveyor controls and action buttons
 */
const TopNavigation = ({
  currentUser,
  velocities,
  editMode,
  toggleEdit,
  setShowSideMenu,
  switchDevice,
  conveyorCode,
  linkedDevices,
  appSettings,
  draftRows,
  draftColumns,
  setDraftRows,
  setDraftColumns,
  onSaveLayout,
  isRecipeControlEnabled = false,
}) => {
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const isOnline = useOnlineStatus();

  // Shared correction state/selectors sourced from RecipeDisplayContext.
  const { newestActionablePendingMaterialCorrection } = useRecipeDisplay();

  const conveyorSelectValue = linkedDevices?.length > 0 ? 'all' : currentUser.device_id;
  const conveyorOptions = [
    ...Object.entries(currentUser?.devices ?? {}).map(([key, value]) => ({
      value: key,
      label: value?.display_name ?? key,
    })),
    ...(Object.keys(currentUser?.devices || {}).length > 1
      ? [{ value: 'all', label: t('feed_timestamping_all_devices') }]
      : []),
  ];

  const velocity = velocities[currentUser?.device_id];
  const speed = velocity?.avg;
  const unit = velocity?.unit;

  return (
    <>
      <div className='flex items-center justify-between gap-4 py-4 px-6 box-border h-[4.75rem] border-b border-b-[#e2e8f0] dark:border-b-[#3f526233] dark:bg-[linear-gradient(180deg,_#2773b73d_25%,_rgba(12,18,29,0.24)_100%)] transition-all duration-300'>
        {/* Show these elements only when NOT in edit mode */}
        {!editMode && (
          <>
            <div className='flex items-center gap-3'>
              {/* Conditionally render conveyor selection dropdown based on settings */}
              {appSettings?.navbar_display?.conveyor_selection_dropdown !== false && (
                <Select
                  className='w-[16rem] min-w-[16rem] max-w-[16rem] shrink-0 font-semibold'
                  value={conveyorSelectValue}
                  options={conveyorOptions}
                  onChange={switchDevice}
                />
              )}

              {/* Conditionally render speed indicator based on settings */}
              {appSettings?.navbar_display?.conveyor_speed_indicator !== false && (
                <div
                  className={cn(
                    'relative flex box-border rounded-lg border bg-white dark:bg-[#0C121D]',
                    speed === undefined
                      ? 'border-[#E8E8EA] dark:border-[#3C3E53]'
                      : speed > 0
                        ? 'border-[#008025] dark:border-[#009236]'
                        : 'border-[#B4000A] dark:border-[#9E2121]',
                  )}
                >
                  <div
                    className={cn(
                      'flex flex-col items-center justify-center px-3 border-r rounded-l-lg',
                      speed === undefined
                        ? 'text-[#5C5E6B] dark:text-[#94A3B8] bg-[#EDF4FF] dark:bg-[#242933] border-r-[#E8E8EA] dark:border-r-[#3C3E53]'
                        : speed > 0
                          ? 'text-[#166534] bg-[#dcfce7] dark:text-[#FAFAFA] dark:bg-[#00923680] border-r-[#008025] dark:border-r-[#009236]'
                          : 'text-[#991b1b] bg-[#fee2e2] dark:text-[#FAFAFA] dark:bg-[#9E212180] border-r-[#B4000A] dark:border-r-[#9E2121]',
                    )}
                  >
                    <Speedometer />
                    <span className='text-sm font-medium'>{conveyorCode}</span>
                  </div>
                  <div className={cn('p-2 pr-3 flex items-center gap-1 min-w-36 justify-center')}>
                    <span
                      className={cn(
                        'text-xl font-medium text-[#5C5E6B] dark:text-[#FAFAFA]',
                        speed === undefined && 'text-[#8A8C95] dark:text-[#94A3B8]',
                      )}
                    >
                      {speed ? Math.round(speed * 100) / 100 : '0.00'}
                    </span>
                    <span className='text-base font-medium text-[#8A8C95] dark:text-[#94A3B8]'>{unit || 'm/min'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Expose the CTA only when recipe-control is enabled and a pending correction exists. */}
            {isRecipeControlEnabled && newestActionablePendingMaterialCorrection && (
              <button
                className='text-base font-semibold text-[#FAFAFA] rounded-lg border border-[#E17100CC] bg-[#EE7D0A] dark:bg-[#EE7D0A80] px-3 py-2 hover:bg-[#EE7D0ACC] active:bg-[#EE7D0AFF] dark:hover:bg-[#EE7D0ACC] dark:active:bg-[#EE7D0AFF] transition-colors duration-300'
                onClick={() => setIsCorrectionModalOpen(true)}
              >
                {t('feed_timestamping_ongoing_correction_button')}
              </button>
            )}

            <div className='flex items-center gap-4'>
              <span
                className='p-1 shrink-0 flex items-center justify-center text-[#454756] dark:text-[#FAFAFA]'
                aria-label={isOnline ? t('online_status_online') : t('online_status_offline')}
                title={isOnline ? t('online_status_online') : t('online_status_offline')}
              >
                {isOnline ? (
                  <CloudDoneOutline
                    className='size-6 motion-reduce:animate-none animate-fadeout-5s'
                    aria-hidden='true'
                  />
                ) : (
                  <CloudOfflineOutline className='size-6' aria-hidden='true' />
                )}
              </span>
              <button
                className='flex items-center gap-2 py-2 pl-3.5 pr-4 rounded-lg dark:bg-[#0C121D] hover:bg-black/5 active:bg-black/10 dark:hover:bg-white/15 dark:active:bg-white/25 border border-[#2563EB] dark:border-[#2773B7] text-[#2563EB] dark:text-[#FAFAFA] text-lg font-medium box-border h-11 transition-colors duration-300'
                onClick={toggleEdit}
              >
                <Pen className='size-5 shrink-0' />
                <span>{t('feed_timestamping_edit_fields')}</span>
              </button>

              <Link
                className='menu-button py-2 px-2.5 rounded-lg border border-[#D0D1D5] dark:border-[#3C3E53] dark:bg-[#0C121D] hover:bg-black/5 active:bg-black/10 dark:hover:bg-white/15 dark:active:bg-white/25 text-[#454756] dark:text-[#FAFAFA] box-border size-11 flex items-center justify-center transition-colors duration-300'
                to='/corrections'
              >
                <Clipboard className='size-5 shrink-0' />
              </Link>

              <button
                className='menu-button py-2 px-2.5 rounded-lg border border-[#D0D1D5] dark:border-[#3C3E53] dark:bg-[#0C121D] hover:bg-black/5 active:bg-black/10 dark:hover:bg-white/15 dark:active:bg-white/25 text-[#454756] dark:text-[#FAFAFA] box-border size-11 flex items-center justify-center transition-colors duration-300'
                onClick={() => setShowSideMenu(true)}
              >
                <Menu className='size-5 shrink-0' />
              </button>
            </div>
          </>
        )}

        {/* Show these elements only when in edit mode */}
        {editMode && (
          <>
            <div className='flex gap-2 items-center'>
              <button
                className='inline-flex items-center gap-2 rounded-lg p-1 pr-3 w-fit transition-colors duration-200 text-[#a2a3ab] hover:bg-black/5 active:bg-black/10 dark:text-[#cad5e2] dark:hover:bg-white/15 dark:active:bg-white/25'
                onClick={toggleEdit}
              >
                <ChevronLeft className='size-6 text-[#A2A3AB] dark:text-[#CAD5E2]' />
                <h2 className='text-xl font-medium text-[#16192C] dark:text-[#FAFAFA]'>
                  {t('feed_timestamping_edit_fields')}
                </h2>
              </button>
            </div>

            <div className='flex items-center gap-3'>
              {/* Row controls */}
              <div className='h-11 flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white dark:border-[#3C3E53] dark:bg-[#0C121D] px-4'>
                <span className='text-base font-medium text-[#5c6b7a] dark:text-[#CAD5E2] mr-2'>
                  {t('feed_timestamping_row')}
                </span>
                <button
                  onClick={() => setDraftRows((prev) => Math.max(prev - 1, 2))}
                  className='bg-[#E8E8EA] dark:bg-[#242933] p-1.5 rounded disabled:pointer-events-none disabled:opacity-75'
                  disabled={draftRows <= 2}
                >
                  <Minus className='size-6 shrink-0 text-[#5C5E6B] dark:text-[#FAFAFA]' />
                </button>
                <span className='text-base font-semibold text-[#2d3041] dark:text-[#FAFAFA]'>{draftRows}</span>
                <button
                  onClick={() => setDraftRows((prev) => Math.min(prev + 1, 4))}
                  className='bg-[#E8E8EA] dark:bg-[#242933] p-1.5 rounded disabled:pointer-events-none disabled:opacity-75'
                  disabled={draftRows >= 3}
                >
                  <Plus className='size-6 shrink-0 text-[#5C5E6B] dark:text-[#FAFAFA]' />
                </button>
              </div>

              {/* Column controls */}
              <div className='h-11 flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white dark:border-[#3C3E53] dark:bg-[#0C121D] px-4'>
                <span className='text-base font-medium text-[#5c6b7a] dark:text-[#CAD5E2] mr-2'>
                  {t('feed_timestamping_column')}
                </span>
                <button
                  onClick={() => setDraftColumns((prev) => Math.max(prev - 1, 2))}
                  className='bg-[#E8E8EA] dark:bg-[#242933] p-1.5 rounded disabled:pointer-events-none disabled:opacity-75'
                  disabled={draftColumns <= 2}
                >
                  <Minus className='size-6 shrink-0 text-[#5C5E6B] dark:text-[#FAFAFA]' />
                </button>
                <span className='text-base font-semibold text-[#2d3041] dark:text-[#FAFAFA]'>{draftColumns}</span>
                <button
                  onClick={() => setDraftColumns((prev) => Math.min(prev + 1, 5))}
                  className='bg-[#E8E8EA] dark:bg-[#242933] p-1.5 rounded disabled:pointer-events-none disabled:opacity-75'
                  disabled={draftColumns >= 4}
                >
                  <Plus className='size-6 shrink-0 text-[#5C5E6B] dark:text-[#FAFAFA]' />
                </button>
              </div>
            </div>

            <div className='flex items-center gap-4'>
              <button
                className='rounded-lg box-border px-4 py-2 text-lg font-medium h-11 text-white dark:text-white bg-[#2563eb] hover:bg-[#235ad2] active:bg-[#1e4fc1] dark:bg-[#2773B7] dark:hover:bg-[#2773B7CC] dark:active:bg-[#2773B7FF] transition-colors duration-300 flex items-center justify-center'
                onClick={() => {
                  onSaveLayout(draftRows, draftColumns);
                  toggleEdit();
                }}
              >
                {t('common_save')}
              </button>
              <Link
                className='menu-button py-2 px-2.5 rounded-lg border border-[#D0D1D5] dark:border-[#3C3E53] dark:bg-[#0C121D] hover:bg-black/5 active:bg-black/10 dark:hover:bg-white/15 dark:active:bg-white/25 text-[#454756] dark:text-[#FAFAFA] box-border size-11 flex items-center justify-center transition-colors duration-300'
                to='/corrections'
              >
                <Clipboard className='size-5 shrink-0' />
              </Link>

              <button
                className='menu-button py-2 px-2.5 rounded-lg border border-[#D0D1D5] dark:border-[#3C3E53] dark:bg-[#0C121D] hover:bg-black/5 active:bg-black/10 dark:hover:bg-white/15 dark:active:bg-white/25 text-[#454756] dark:text-[#FAFAFA] box-border size-11 flex items-center justify-center transition-colors duration-300'
                onClick={() => setShowSideMenu(true)}
              >
                <Menu className='size-5 shrink-0' />
              </button>
            </div>
          </>
        )}
      </div>

      <ProductionCorrectionsModal
        disableAutoPick
        forcedCorrection={
          isRecipeControlEnabled && isCorrectionModalOpen ? newestActionablePendingMaterialCorrection?.correction : null
        }
        onClose={() => setIsCorrectionModalOpen(false)}
      />
    </>
  );
};

export default TopNavigation;
